import React from "react";
import { Checkbox, FormControlLabel, FormHelperText, Grid, Typography, WithStyles } from "@material-ui/core";
import { styles } from "./A7PlatformAgreement.styles";
import { withStyles } from "@material-ui/styles";
import { FormTextField } from "../../components/FormComponents/FormTextField";
import { createTarget, phonePattern } from "../../../utilities/generalUtils";
import { FormNativeSelect } from "../../components/FormComponents/FormNativeSelect";
import { FormDateField } from "../../components/FormComponents/FormDateField";
import { today } from "../../../utilities/dateTimeUtils";
import { DATE_FORMAT } from "../../../constants/TIME";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { useSelector } from "react-redux";
import { isEuCountry } from "../../../utilities/countriesUtils";
import { findCategoryByName } from "../productOverview/ProductHelper";
import { selectVisibleCountries } from "../../../selectors/countries/CountriesSelector";

export interface IA7PlatformAgreementProps extends WithStyles<typeof styles> {
    handleChange: any;
    details: any;
    product: any;
    errors: any;
    handleSubmit: any;
    hasNonEditableFields: boolean;
}

const VALIDATION_GENERAL = "Please enter and check all required fields";

const A7PlatformAgreementPure: React.FunctionComponent<IA7PlatformAgreementProps> = ({
    handleChange,
    details,
    product,
    errors,
    handleSubmit,
    hasNonEditableFields,
    classes
}) => {

    const countries = useSelector(state => selectVisibleCountries(state));
    const a7productsInfoFromState = useSelector((state) => state.a7products);

    const a7productsInfo = {
        products: a7productsInfoFromState?.products?.filter(product => product.id.includes("A7")),
        categories: a7productsInfoFromState?.categories?.filter(category => category.id.includes("A7"))
    };

    const availableProducts = a7productsInfo?.products?.map(p => ({name: p.name, shortcut: p.id}));
    const thisCategory = findCategoryByName(a7productsInfo?.categories, product?.category);

    const formIsValid = () => {
        return (!isEuCountry(countries, details.country) || details.vat)
            && details.companyEmail
            && details.firstName
            && details.lastName
            && details.companyName
            && details.street
            && details.postalCode
            && details.city
            && details.country
            && details.product
            && details.desiredEffectiveDate
            && details.confirmLegalEntity
            && details.termsConditions
            && Object.values(errors).every(x => x === "");
    };

    const errorMessage = Object.keys.length > 0 ? Object.values(errors).filter(e => e !== "").join(", ") : VALIDATION_GENERAL;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h3">A7 Platform Trial Agreement</Typography>
                <Typography variant="body2">This A7 Platform Trial Agreement (hereinafter “A7 Trial Agreement”) is
                    concluded between Deutsche Börse AG, Mergenthalerallee 61, 65760 Eschborn, Germany and Client (as provided
                    under client contact details). Any capitalized terms not specifically defined herein shall have the same
                    meaning as defined in the A7 Trial Terms.</Typography>
                <br/>
                <Typography variant="body2">This A7 Platform provides full on-the-fly order book reconstruction, allowing
                    customers to analyse past trading events with full granularity and run complex computations on selected
                    instruments. The service will be offered as a web-based comprehensive service including detailed historic data
                    on selected instruments and partially customizable tools for customers’ analysis. The service will be
                    accessible via a browser-based front-end solution as well as through an application programming interface
                    (API), allowing direct access to data and functions (hereinafter “A7 Platform”).
                </Typography>
                <br/>
                <Typography variant="body2">
                    Client wishes to access and use the A7 Platform for its own internal business purposes on a trial basis for a
                    limited time and limited users, free of charge, as further specified in this A7 Trial Agreement.
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="h4">Client contact details</Typography>
            </Grid>
            <Grid item xs={2}>
                <FormTextField
                    id="title"
                    onChange={handleChange}
                    value={details.title}
                    label={"Title"}
                />
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="firstName"
                    onChange={handleChange}
                    value={details.firstName}
                    label={"First name"}
                    required
                    disabled={hasNonEditableFields}
                />
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="lastName"
                    onChange={handleChange}
                    value={details.lastName}
                    label={"Last name"}
                    required
                    disabled={hasNonEditableFields}
                />
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="companyName"
                    onChange={handleChange}
                    value={details.companyName}
                    label={"Company name & Legal form"}
                    required
                    disabled={hasNonEditableFields}
                />
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="companyPhone"
                    onChange={handleChange}
                    value={details.companyPhone}
                    label={"Company phone"}
                    pattern={phonePattern}
                />
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="companyEmail"
                    onChange={handleChange}
                    value={details.companyEmail}
                    label={"Company email"}
                    required
                />
            </Grid>
            <Grid item xs={12}>
                <Typography variant="h4">Legal address</Typography>
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="street"
                    onChange={handleChange}
                    value={details.street}
                    label={"Street"}
                    required
                />
            </Grid>
            <Grid item xs={2}>
                <FormTextField
                    id="postalCode"
                    onChange={handleChange}
                    value={details.postalCode}
                    label={"Postal code"}
                    required
                />
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="city"
                    onChange={handleChange}
                    value={details.city}
                    label={"City"}
                    required
                />
            </Grid>
            <Grid item xs={4}>
                <FormNativeSelect label={"Country"} options={countries} onChange={handleChange}
                                  id="country" value={details.country}
                                  required
                                  emptyCaption={"Select country"}
                />
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="vat"
                    onChange={handleChange}
                    value={details.vat}
                    label={"VAT no. (EU only)"}
                />
            </Grid>
            <Grid item xs={12}>
                <Typography variant="h4">Product</Typography>
            </Grid>
            <Grid item xs={4}>
                <FormNativeSelect
                    label={"product"}
                    options={availableProducts}
                    onChange={handleChange}
                    id="product"
                    value={details.product}
                    required
                />
            </Grid>
            <Grid item xs={8}>
                <Typography variant="body2">
                    {thisCategory?.legalDisclaimer}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant={"h6"}>Desired effective date **</Typography>
                <FormDateField
                    label="Date"
                    variant="filled"
                    disabled={false}
                    placeholder={"Desired effective date"}
                    value={details.desiredEffectiveDate}
                    firstAvailableDate={today().format(DATE_FORMAT)}
                    lastAvailableDate={today().add(3, "months").format(DATE_FORMAT)}
                    onChange={newValue => handleChange(createTarget("desiredEffectiveDate", newValue))}
                />
            </Grid>

            <Grid container item xs={12} spacing={2}>
                <Grid item xs={12}>
                    <Typography variant={"h6"}>Message</Typography>
                </Grid>
                <Grid item xs={6}>
                    <FormTextField
                        id="message"
                        multiline
                        rows={8}
                        onChange={handleChange}
                        value={details.message}
                        label={"Please name authorised users (first name, last name, email) which shall have access during the trial period."}
                    />
                </Grid>
            </Grid>
            <Grid item xs={12} className={classes.summary}>
                <Typography variant="h2">Summary</Typography>
            </Grid>
            <Grid container item xs={12}>
                <Grid item xs={2}>
                    <Typography variant="h4">Product</Typography>
                    <Typography
                        variant="body2">{details.product ? availableProducts.find(p => p.shortcut === details.product).name : "–––"}</Typography>
                </Grid>
                {product?.trial
                    ? <Grid item xs={2}>
                        <Typography variant="h4">Trial period</Typography>
                        <Typography
                            variant="body2">{product?.trialPeriodInWeeks} {product?.trialPeriodInWeeks === 1 ? "week" : "weeks"}</Typography>
                    </Grid>
                    : null}
                <Grid item xs={2}>
                    <Typography variant="h4">Price</Typography>
                    <Typography variant="body2">{product?.price} EUR</Typography>
                </Grid>
            </Grid>
            <Grid container item xs={12} className={classes.checkboxes}>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={<Checkbox checked={details.termsConditions !== ""} onChange={handleChange}
                                           name="termsConditions" value={details.termsConditions ? "" : "on"} />}
                        label={<Typography
                            variant="body2">I have read and accepted the <a
                            href={"/a7/A7 Platform Trial Terms and Conditions_DBAG_version1.2.pdf"}
                            rel="noreferrer"
                            target="_blank">A7 Platform Trial Terms and Conditions</a>.*</Typography>}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={<Checkbox checked={details.confirmLegalEntity !== ""} onChange={handleChange}
                                           name="confirmLegalEntity"
                                           value={details.confirmLegalEntity ? "" : "on"} />}
                        label={<Typography
                            variant="body2">{"I hereby confirm that the client is a legal entity.*"}</Typography>}
                    />
                </Grid>
            </Grid>
            <Grid item xs={2}>
                <BlueButton onClick={handleSubmit} disabled={!formIsValid()}>
                    {"Send"}
                </BlueButton>
                <FormHelperText error={!formIsValid()}>{errorMessage}</FormHelperText>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="body2">* Required fields</Typography>
                {product?.trial ? <Typography
                        variant="body2">** {product?.remark}</Typography>
                    : null}
            </Grid>
        </Grid>
    );
};

export const A7PlatformAgreement = withStyles(styles)(A7PlatformAgreementPure);